// @ts-nocheck

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';
import styled from 'styled-components';

import { Target } from '../../client';
import { ExpandedBetGroupContext } from '../../contexts/ExpandedBetGroupContext';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import { ReactHeight } from '../../lib/react-height.custom';
import useWinabetLibContext from '../../winabet-lib/hooks/useWinabetLibContext';
import RowFactoryContainer from '../RowFactory/RowFactoryContainer';

const Wrapper = styled.div`
  .ReactVirtualized__Grid,
  .ReactVirtualized__Grid__innerScrollContainer {
    overflow: visible !important;
    outline: none !important;
  }
`;

const RowWrapper = styled.div`
  &:not(:first-child) {
    --not-first-padding-top: ${Target.__MOBILE__ ? '0.8em' : '1em'};
    --not-first-padding-bottom: ${Target.__MOBILE__ ? '0.2em' : '1em'};
  }
`;

const getElementHeight = (el) => el.offsetHeight;

const overscanIndicesGetter = (options) => {
  const { cellCount, overscanCellsCount, scrollDirection, startIndex, stopIndex } = options;

  let overscanStartIndex = 0;
  let overscanStopIndex = 0;
  switch (scrollDirection) {
    case 1:
    case -1:
      overscanStartIndex = startIndex - overscanCellsCount;
      overscanStopIndex = stopIndex + overscanCellsCount;
      if (overscanStartIndex < 0) {
        overscanStopIndex += Math.abs(overscanStartIndex);
      }
      break;
    default:
      break;
  }
  return {
    overscanStartIndex: Math.max(0, overscanStartIndex),
    overscanStopIndex: Math.min(cellCount - 1, overscanStopIndex),
  };
};

const MatchRowList = (props: unknown) => {
  const { items, anchorMatchId, scrollTop, setScrollTop, largeMissionCard } = props;
  const { navbarHeight } = useViewportContext();
  const cache = useRef({});
  const renderListRef = useRef();
  const winabetLibContext = useWinabetLibContext();

  const [expandedBetGroups, setExpandedBetGroups] = useState({});

  const scrollElement = useMemo(() => {
    if (Target.__LIBRARY__) {
      return winabetLibContext?.parentRef.current;
    }

    if (Target.__MOBILE__) {
      return getAppDOMElement();
    }

    return window;
  }, [winabetLibContext]);

  const [indexToScrollTo, setIndexToScrollTo] = useState(
    items.findIndex((match) => match.matchId === parseInt(anchorMatchId), 10),
  );

  useEffect(() => {
    if (scrollTop && renderListRef && renderListRef.current) {
      scrollElement.scroll?.({ top: 0 });
      setScrollTop(false);
    }
  }, [scrollTop, setScrollTop, scrollElement, navbarHeight, renderListRef]);

  useEffect(() => {
    if (indexToScrollTo && renderListRef && renderListRef.current) {
      renderListRef.current.scrollToRow(indexToScrollTo);
      requestAnimationFrame(() => {
        if (renderListRef.current) {
          renderListRef.current.scrollToRow(indexToScrollTo);
          scrollElement.scrollBy?.(0, -navbarHeight);
        }
      });
      setIndexToScrollTo(undefined);
    }
  }, [indexToScrollTo, renderListRef, scrollElement, navbarHeight]);

  const rowHeight = ({ index }) => {
    return cache.current[index] || (Target.__MOBILE__ ? 164 : 55);
  };

  const renderRow = ({ index, key, style, width }) => {
    const item = items[index];

    const { matchId } = item;

    return (
      <RowWrapper style={style} key={key}>
        <ReactHeight
          getElementHeight={getElementHeight}
          onHeightReady={(height) => {
            cache.current[index] = height;
            if (renderListRef.current) {
              renderListRef.current.recomputeRowHeights(index);
            }
          }}
          style={{ width }}
        >
          <RowFactoryContainer expandId={`${matchId}`} {...item} largeMissionCard={largeMissionCard} />
          <ReactResizeDetector
            handleHeight
            onResize={(w, height) => {
              cache.current[index] = height;
              if (renderListRef.current) {
                renderListRef.current.recomputeRowHeights(index);
              }
            }}
          />
        </ReactHeight>
      </RowWrapper>
    );
  };

  const forwardedState = useMemo(
    () => ({ expandedBetGroups, setExpandedBetGroups }),
    [expandedBetGroups, setExpandedBetGroups],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <ExpandedBetGroupContext.Provider value={forwardedState}>
        <WindowScroller scrollElement={scrollElement}>
          {({ height, scrollTop, onChildScroll }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  ref={renderListRef}
                  autoHeight
                  height={height || 0}
                  width={width || 0}
                  rowCount={items.length}
                  overscanRowCount={Target.__MOBILE__ ? 5 : 10}
                  overscanIndicesGetter={overscanIndicesGetter}
                  rowHeight={rowHeight}
                  rowRenderer={renderRow}
                  scrollToAlignment="start"
                  scrollToIndex={indexToScrollTo}
                  scrollTop={scrollTop}
                  items={items}
                  onScroll={onChildScroll}
                />
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      </ExpandedBetGroupContext.Provider>
    </Wrapper>
  );
};

export default MatchRowList;
